import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { PropsWithChildren } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';

import { cn } from '@/utils/classname';
import { useStorage } from '@/utils/storage/useStorage.util';

type FileReadType = 'string' | 'arrayBuffer';

type FileResult<T extends FileReadType> = T extends 'string'
  ? string
  : ArrayBuffer;

type FileObj<T extends FileReadType> = {
  content: FileResult<T>;
  file: File;
};

export type Files<T extends FileReadType> = FileObj<T>[];

interface Props<T extends FileReadType> extends PropsWithChildren {
  onFilesDrop: (files: Files<T>) => void;
  title?: string;
  subTitle?: string;
  className?: string;
  classNameDrag?: string;
  type: T;
  accept?: DropzoneOptions['accept'];
  errorMessage?: string;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
  'data-testid'?: string;
}

export function Dropzone<T extends FileReadType>(props: Props<T>) {
  const {
    onFilesDrop,
    title = 'Drop files here',
    subTitle = 'Suggest file types: TXT, CSV, JSON, or XML',
    children,
    className,
    classNameDrag,
    accept,
    errorMessage: errorMessageProp,
    setErrorMessage: setErrorMessageProp,
    'data-testid': dataTestId,
  } = props;

  const [errorMessage, setErrorMessage] = useStorage<string>(
    { onParentStateChange: setErrorMessageProp, parentState: errorMessageProp },
    ''
  );

  const handleDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ): Promise<void> => {
    setErrorMessage(''); // Clear error if file is valid

    const fileRejectionsLength = fileRejections.length;
    // Handle rejected files
    if (fileRejectionsLength > 0) {
      if (fileRejectionsLength > 1) {
        setErrorMessage(`Too many files. Please upload only one file.`);
      } else {
        setErrorMessage(
          `Invalid file format: ${fileRejections.map(({ file }) => file.name).join(', ')}.`
        );
      }
    } else {
      const file = acceptedFiles[0];
      const reader = new FileReader();

      reader.onload = () => {
        const content = reader.result as FileResult<T>;
        onFilesDrop([{ content, file }]);
      };
      reader.onerror = () => {
        setErrorMessage(`Failed to read file: ${file.name}.`);
      };

      if (props.type === 'string') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    accept,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-chariot-stroke flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-all duration-300 ease-in-out outline-none',
        isDragActive ? 'bg-chariot-background-secondary' : '',
        className,
        isDragActive && classNameDrag
      )}
      data-testid={dataTestId}
    >
      <input
        {...getInputProps()}
        data-testid={dataTestId ? `${dataTestId}-input` : undefined}
      />
      {children || (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center"
          >
            <PlusCircleIcon className="size-10 text-gray-400" />
            <h3 className="text-chariot-text-primary mt-4 font-semibold">
              {title}
            </h3>
            <p className="text-chariot-text-primary mt-1 text-sm">{subTitle}</p>
          </motion.div>
        </>
      )}

      {errorMessage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="px-4 py-2 text-center"
        >
          <p className="text-sm font-medium text-red-600">{errorMessage}</p>
        </motion.div>
      )}
    </div>
  );
}
